import { useEffect, useMemo, useRef, useState } from "react";
import { CronExpressionParser } from "cron-parser";
import cronstrue from "cronstrue";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  Globe,
  Plus,
  X,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { lookupTimezone as lookupZone } from "@/lib/timezones";
import { TimezoneSelect } from "@/components/shared/TimezoneSelect";

type Mode = "interval" | "times" | "monthly";
type IntervalUnit = "minutes" | "hours";

interface TimeOfDay {
  hour: number;
  minute: number;
}

const MINUTE_INTERVALS = [1, 2, 5, 10, 15, 20, 30, 45, 60];
const HOUR_INTERVALS = [1, 2, 3, 4, 6, 8, 12, 24];
const MAX_TIMES = 6;

// Cron values: 0 = Sunday, 1 = Monday, …, 6 = Saturday.
// Visual order starts on Monday.
const VISUAL_DAYS: Array<{ cron: number; label: string; aria: string }> = [
  { cron: 1, label: "Mon", aria: "Monday" },
  { cron: 2, label: "Tue", aria: "Tuesday" },
  { cron: 3, label: "Wed", aria: "Wednesday" },
  { cron: 4, label: "Thu", aria: "Thursday" },
  { cron: 5, label: "Fri", aria: "Friday" },
  { cron: 6, label: "Sat", aria: "Saturday" },
  { cron: 0, label: "Sun", aria: "Sunday" },
];


export interface CronSchedulerValue {
  cron: string;
  timezone: string;
  valid: boolean;
}

interface CronSchedulerBuilderProps {
  /** Current cron expression — used to seed the builder state on mount.
   *  Subsequent edits flow through `onChange`; we don't re-detect from
   *  the prop after mount because that would clobber the user's
   *  in-flight edits. */
  cron: string;
  timezone: string;
  startsAt?: string | null;
  endsAt?: string | null;
  onChange: (next: CronSchedulerValue) => void;
  /** When set, replaces the internal validation message — the parent
   *  uses this to surface server-side validation errors. */
  errorOverride?: string;
}

export function CronSchedulerBuilder({
  cron,
  timezone,
  startsAt,
  endsAt,
  onChange,
  errorOverride,
}: CronSchedulerBuilderProps) {
  const initial = useMemo(() => detectInitial(cron), []);

  const [mode, setMode] = useState<Mode>(initial.mode);
  const [intervalValue, setIntervalValue] = useState<number>(
    initial.intervalValue,
  );
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(
    initial.intervalUnit,
  );
  const [times, setTimes] = useState<TimeOfDay[]>(initial.times);
  const [dows, setDows] = useState<number[]>(initial.dows);
  const [dayOfMonth, setDayOfMonth] = useState<number>(initial.dayOfMonth);
  const [monthlyTime, setMonthlyTime] = useState<TimeOfDay>(
    initial.monthlyTime,
  );
  const [tz, setTz] = useState<string>(timezone || "America/New_York");
  const [advanced, setAdvanced] = useState<boolean>(initial.isAdvanced);
  const [customCron, setCustomCron] = useState<string>(cron || "");

  // Compute the cron the builder produces. Returns either a string or
  // a structured error so the UI can react (disable save, show hint).
  const builderResult = useMemo<{
    cron: string | null;
    error: string | null;
  }>(() => {
    if (advanced) {
      const trimmed = customCron.trim();
      if (!trimmed) return { cron: null, error: "Enter a cron expression" };
      return { cron: trimmed, error: null };
    }
    // Monthly is the one mode that doesn't AND with a day-of-week
    // filter — a calendar day and a weekday pattern can't both be
    // restrictive without cron's confusing dom/dow OR semantics, so
    // monthly pins the day-of-week field to `*`.
    if (mode === "monthly") {
      if (
        !Number.isInteger(dayOfMonth) ||
        dayOfMonth < 1 ||
        dayOfMonth > 31
      ) {
        return { cron: null, error: "Pick a day of the month (1–31)" };
      }
      return {
        cron: `${monthlyTime.minute} ${monthlyTime.hour} ${dayOfMonth} * *`,
        error: null,
      };
    }
    if (dows.length === 0) {
      return { cron: null, error: "Pick at least one day" };
    }
    const dowField = formatDows(dows);
    if (mode === "interval") {
      if (!Number.isInteger(intervalValue) || intervalValue < 1) {
        return { cron: null, error: "Interval must be at least 1" };
      }
      if (intervalUnit === "minutes") {
        if (intervalValue > 60) {
          return { cron: null, error: "Minute interval can't exceed 60" };
        }
        const minField = intervalValue === 1 ? "*" : `*/${intervalValue}`;
        return { cron: `${minField} * * * ${dowField}`, error: null };
      }
      // hours
      if (intervalValue > 24) {
        return { cron: null, error: "Hour interval can't exceed 24" };
      }
      const hourField = intervalValue === 1 ? "*" : `*/${intervalValue}`;
      return { cron: `0 ${hourField} * * ${dowField}`, error: null };
    }
    // mode === "times"
    if (times.length === 0) {
      return { cron: null, error: "Add at least one time" };
    }
    const minutes = new Set(times.map((t) => t.minute));
    if (minutes.size > 1) {
      // Cron's 5-field syntax can't express e.g. (06:00, 08:30) with a
      // single expression — minute and hour fields are AND'd, so
      // listing 0,30 in the minute field with 6,8 in the hour field
      // would also match 06:30 and 08:00.
      return {
        cron: null,
        error:
          "All times must share the same minute (e.g. all on :00 or all on :30), cron can't express mixed minutes in one schedule.",
      };
    }
    const minute = times[0].minute;
    const hours = Array.from(new Set(times.map((t) => t.hour))).sort(
      (a, b) => a - b,
    );
    const hourField = hours.length === 1 ? String(hours[0]) : hours.join(",");
    return {
      cron: `${minute} ${hourField} * * ${dowField}`,
      error: null,
    };
  }, [
    advanced,
    customCron,
    mode,
    intervalValue,
    intervalUnit,
    times,
    dows,
    dayOfMonth,
    monthlyTime,
  ]);

  // Validate via cron-parser regardless of where the cron came from.
  const validation = useMemo(() => {
    if (!builderResult.cron) {
      return { valid: false, error: builderResult.error };
    }
    try {
      CronExpressionParser.parse(builderResult.cron, { tz });
      return { valid: true, error: null as string | null };
    } catch (err) {
      return {
        valid: false,
        error:
          err instanceof Error ? err.message : "Invalid cron expression",
      };
    }
  }, [builderResult, tz]);

  // Human-readable description from cronstrue.
  const humanDescription = useMemo<string | null>(() => {
    if (!builderResult.cron || !validation.valid) return null;
    try {
      return cronstrue.toString(builderResult.cron, { use24HourTimeFormat: true });
    } catch {
      return null;
    }
  }, [builderResult.cron, validation.valid]);

  // Notify the parent on every committed change. The ref pattern keeps
  // a non-memoised onChange from looping the effect.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  useEffect(() => {
    onChangeRef.current({
      cron: builderResult.cron ?? "",
      timezone: tz,
      valid: validation.valid,
    });
  }, [builderResult.cron, tz, validation.valid]);

  // Next 3 executions in the chosen timezone, gated by the optional
  // start/end window.
  const preview = useMemo(() => {
    if (!builderResult.cron || !validation.valid) {
      return { runs: [] as Date[], truncatedByEnd: false };
    }
    const now = new Date();
    const startsDate = startsAt ? new Date(startsAt) : null;
    const endsDate = endsAt ? new Date(endsAt) : null;
    const currentDate =
      startsDate && startsDate.getTime() > now.getTime() ? startsDate : now;
    try {
      const interval = CronExpressionParser.parse(builderResult.cron, {
        tz,
        currentDate,
        endDate: endsDate ?? undefined,
      });
      const runs: Date[] = [];
      let truncated = false;
      for (let i = 0; i < 3; i++) {
        try {
          runs.push(interval.next().toDate());
        } catch {
          truncated = true;
          break;
        }
      }
      return { runs, truncatedByEnd: truncated };
    } catch {
      return { runs: [], truncatedByEnd: false };
    }
  }, [builderResult.cron, validation.valid, tz, startsAt, endsAt]);

  // Local time tick for the timezone hint.
  const [tickNow, setTickNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setTickNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const zoneCity = lookupZone(tz)?.city ?? tz;
  const localTime = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: tz,
      }).format(tickNow),
    [tz, tickNow],
  );

  const showError =
    errorOverride ??
    (!validation.valid && validation.error ? validation.error : null);

  // ─── Times helpers ────────────────────────────────────────────────
  function addTime() {
    if (times.length >= MAX_TIMES) return;
    const last = times[times.length - 1];
    const nextHour = last ? Math.min(23, last.hour + 6) : 9;
    setTimes([
      ...times,
      { hour: nextHour, minute: last?.minute ?? 0 },
    ]);
  }
  function removeTime(idx: number) {
    setTimes(times.filter((_, i) => i !== idx));
  }
  function updateTime(idx: number, value: string) {
    const parsed = parseHHMM(value);
    if (!parsed) return;
    setTimes(times.map((t, i) => (i === idx ? parsed : t)));
  }

  // ─── Day helpers ──────────────────────────────────────────────────
  function toggleDow(d: number) {
    setDows((prev) =>
      prev.includes(d)
        ? prev.filter((x) => x !== d)
        : [...prev, d].sort((a, b) => a - b),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Schedule
        </label>
      </div>

      {!advanced && (
        <>
          {/* Mode selector — Interval / Specific times / Monthly. */}
          <div className="grid grid-cols-3 gap-2">
            <ModeButton
              active={mode === "interval"}
              onClick={() => setMode("interval")}
              label="Run every"
              hint={
                intervalUnit === "minutes"
                  ? `${intervalValue} min`
                  : `${intervalValue} h`
              }
            />
            <ModeButton
              active={mode === "times"}
              onClick={() => setMode("times")}
              label="At specific times"
              hint={
                times.length === 0
                  ? "—"
                  : times
                      .map((t) => `${pad(t.hour)}:${pad(t.minute)}`)
                      .join(", ")
              }
            />
            <ModeButton
              active={mode === "monthly"}
              onClick={() => setMode("monthly")}
              label="Monthly on day"
              hint={`Day ${dayOfMonth} · ${pad(monthlyTime.hour)}:${pad(
                monthlyTime.minute,
              )}`}
            />
          </div>

          {mode === "interval" && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Run every
              </label>
              <div className="flex items-center gap-2">
                <Select
                  value={String(intervalValue)}
                  onValueChange={(v) => setIntervalValue(Number(v))}
                >
                  <SelectTrigger className="h-9 w-[88px] rounded-lg border-slate-200 font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(intervalUnit === "minutes"
                      ? MINUTE_INTERVALS
                      : HOUR_INTERVALS
                    ).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={intervalUnit}
                  onValueChange={(v) => {
                    const next = v as IntervalUnit;
                    setIntervalUnit(next);
                    // Snap interval value to a sensible default for the
                    // new unit if the current value isn't in the unit's
                    // canonical option list.
                    const opts =
                      next === "minutes" ? MINUTE_INTERVALS : HOUR_INTERVALS;
                    if (!opts.includes(intervalValue)) {
                      setIntervalValue(next === "minutes" ? 5 : 1);
                    }
                  }}
                >
                  <SelectTrigger className="h-9 w-[140px] rounded-lg border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">minutes</SelectItem>
                    <SelectItem value="hours">hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {mode === "times" && (
            <div className="space-y-2">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Run at
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {times.map((t, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white pl-2 pr-1"
                  >
                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                    <Input
                      type="time"
                      value={`${pad(t.hour)}:${pad(t.minute)}`}
                      onChange={(e) => updateTime(idx, e.target.value)}
                      className="h-8 w-[88px] border-0 bg-transparent px-1 font-mono shadow-none focus-visible:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => removeTime(idx)}
                      aria-label="Remove time"
                      className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {times.length < MAX_TIMES && (
                  <button
                    type="button"
                    onClick={addTime}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white px-3 text-xs font-medium text-slate-600 transition-colors hover:border-indigo-300 hover:text-indigo-600"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add time
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                Cron limitation: every time must share the same minute
                (e.g. 06:00, 12:00, 18:00).
              </p>
            </div>
          )}

          {mode === "monthly" && (
            <div className="space-y-2">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                On day of month
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={String(dayOfMonth)}
                  onValueChange={(v) => setDayOfMonth(Number(v))}
                >
                  <SelectTrigger className="h-9 w-[88px] rounded-lg border-slate-200 font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-500">
                  of each month, at
                </span>
                <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white pl-2 pr-1">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  <Input
                    type="time"
                    value={`${pad(monthlyTime.hour)}:${pad(monthlyTime.minute)}`}
                    onChange={(e) => {
                      const parsed = parseHHMM(e.target.value);
                      if (parsed) setMonthlyTime(parsed);
                    }}
                    className="h-8 w-[88px] border-0 bg-transparent px-1 font-mono shadow-none focus-visible:ring-0"
                  />
                </div>
              </div>
              {dayOfMonth > 28 && (
                <p className="text-[11px] text-amber-700">
                  Day {dayOfMonth} doesn&rsquo;t exist in every month, the job
                  skips months that are too short.
                </p>
              )}
            </div>
          )}

          {/* Days of week — not applicable to the monthly mode, which
           *  pins to a calendar day rather than a weekday pattern. */}
          {mode !== "monthly" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                On days
              </label>
              <div className="flex gap-3 text-xs">
                <PresetLink onClick={() => setDows([1, 2, 3, 4, 5])}>
                  Weekdays
                </PresetLink>
                <PresetLink onClick={() => setDows([0, 1, 2, 3, 4, 5, 6])}>
                  Every day
                </PresetLink>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {VISUAL_DAYS.map(({ cron: dayCron, label, aria }) => {
                const active = dows.includes(dayCron);
                return (
                  <button
                    key={dayCron}
                    type="button"
                    onClick={() => toggleDow(dayCron)}
                    aria-label={aria}
                    aria-pressed={active}
                    className={cn(
                      "flex h-9 min-w-[3rem] items-center justify-center rounded-full border px-3 text-xs font-medium transition-colors",
                      active
                        ? "border-indigo-500 bg-indigo-500 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Timezone */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
              <Globe className="h-3 w-3" />
              Timezone
            </label>
            <TimezoneSelect value={tz} onValueChange={setTz} />
            <p className="text-[11px] text-slate-400">
              It&rsquo;s currently {localTime} in {zoneCity}
            </p>
          </div>
        </>
      )}

      {advanced && (
        <div className="space-y-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Cron expression
          </label>
          <Input
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            placeholder="*/5 * * * *"
            spellCheck={false}
            className={cn(
              "h-10 rounded-lg border-slate-200 font-mono",
              !validation.valid &&
                customCron.trim() !== "" &&
                "border-rose-400 focus-visible:ring-rose-300",
            )}
          />
          <p className="text-[11px] text-slate-400">
            5 fields: minute hour day-of-month month day-of-week
          </p>
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
              <Globe className="h-3 w-3" />
              Timezone
            </label>
            <TimezoneSelect value={tz} onValueChange={setTz} />
          </div>
        </div>
      )}

      {/* Preview block — cron + human description + next runs */}
      {(builderResult.cron || advanced) && (
        <div className="space-y-2 rounded-lg border border-slate-900/[0.06] bg-slate-50/60 px-3 py-2.5">
          {builderResult.cron && (
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Cron
              </div>
              <code className="mt-0.5 block font-mono text-xs text-slate-600">
                {builderResult.cron}{" "}
                <span className="text-slate-400">({tz})</span>
              </code>
            </div>
          )}
          {humanDescription && (
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                In plain English
              </div>
              <p className="mt-0.5 text-xs text-slate-700">
                {humanDescription}
              </p>
            </div>
          )}
          {preview.runs.length > 0 && (
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Next runs
              </div>
              <ul className="mt-0.5 space-y-0.5 text-xs text-slate-600">
                {preview.runs.map((d, i) => (
                  <li key={i} className="font-mono">
                    {formatInTz(d, tz)}
                  </li>
                ))}
              </ul>
              {preview.truncatedByEnd && (
                <p className="mt-1 text-[11px] text-amber-700">
                  End date stops the schedule before further runs.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {showError && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{showError}</span>
        </div>
      )}

      {/* Advanced toggle — hidden by default. Once enabled, the
       *  builder UI hides and the user gets a raw cron input. */}
      <button
        type="button"
        onClick={() => setAdvanced((prev) => !prev)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
      >
        {advanced ? (
          <>
            <ChevronUp className="h-3.5 w-3.5" />
            Back to builder
          </>
        ) : (
          <>
            <ChevronDown className="h-3.5 w-3.5" />
            Advanced: edit cron expression
          </>
        )}
      </button>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────

function ModeButton({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors",
        active
          ? "border-indigo-300 bg-indigo-50/60"
          : "border-slate-200 bg-white hover:border-slate-300",
      )}
    >
      <span
        className={cn(
          "text-sm font-medium",
          active ? "text-indigo-700" : "text-slate-700",
        )}
      >
        {label}
      </span>
      <span className="mt-0.5 truncate text-[11px] text-slate-500">
        {hint}
      </span>
    </button>
  );
}

function PresetLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-indigo-600 hover:text-indigo-500 hover:underline"
    >
      {children}
    </button>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function parseHHMM(value: string): TimeOfDay | null {
  const m = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { hour, minute };
}

function formatDows(dows: number[]): string {
  if (dows.length === 7) return "*";
  const sorted = [...new Set(dows)].sort((a, b) => a - b);
  // Detect a contiguous range so e.g. Mon-Fri renders as `1-5` rather
  // than `1,2,3,4,5`. cronstrue produces nicer English from the range.
  if (sorted.length >= 2) {
    let isContiguous = true;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) {
        isContiguous = false;
        break;
      }
    }
    if (isContiguous) {
      return `${sorted[0]}-${sorted[sorted.length - 1]}`;
    }
  }
  return sorted.join(",");
}

function parseList(value: string, min: number, max: number): number[] | null {
  const out = new Set<number>();
  for (const part of value.split(",")) {
    if (/^\d+$/.test(part)) {
      const n = Number(part);
      if (n < min || n > max) return null;
      out.add(n);
    } else if (/^\d+-\d+$/.test(part)) {
      const [a, b] = part.split("-").map(Number);
      if (a < min || b > max || a > b) return null;
      for (let i = a; i <= b; i++) out.add(i);
    } else {
      return null;
    }
  }
  return Array.from(out).sort((a, b) => a - b);
}

function parseDow(dow: string): number[] | null {
  const out = new Set<number>();
  for (const part of dow.split(",")) {
    if (/^\d+$/.test(part)) {
      const n = Number(part);
      if (n < 0 || n > 7) return null;
      // cron treats 0 and 7 as Sunday.
      out.add(n === 7 ? 0 : n);
    } else if (/^\d+-\d+$/.test(part)) {
      const [a, b] = part.split("-").map(Number);
      if (a < 0 || b < 0 || a > 7 || b > 7 || a > b) return null;
      for (let i = a; i <= b; i++) out.add(i === 7 ? 0 : i);
    } else if (part === "*") {
      return [0, 1, 2, 3, 4, 5, 6];
    } else {
      return null;
    }
  }
  return Array.from(out).sort((a, b) => a - b);
}

function formatInTz(d: Date, tz: string): string {
  const datePart = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: tz,
  }).format(d);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
    timeZoneName: "short",
  }).format(d);
  return `${datePart} at ${timePart}`;
}

interface InitialState {
  mode: Mode;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  times: TimeOfDay[];
  dows: number[];
  dayOfMonth: number;
  monthlyTime: TimeOfDay;
  isAdvanced: boolean;
}

/** Detect builder state from an existing cron expression. The detector
 *  recognises the patterns the builder itself produces (interval +
 *  specific-times + day-of-week filters); anything else opens in the
 *  advanced raw-cron tab so we don't silently rewrite an unsupported
 *  schedule. */
function detectInitial(cron: string): InitialState {
  const fallback: InitialState = {
    mode: "interval",
    intervalValue: 5,
    intervalUnit: "minutes",
    times: [{ hour: 9, minute: 0 }],
    dows: [1, 2, 3, 4, 5],
    dayOfMonth: 1,
    monthlyTime: { hour: 9, minute: 0 },
    isAdvanced: false,
  };
  if (!cron) return fallback;
  const trimmed = cron.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return { ...fallback, isAdvanced: true };
  const [minStr, hourStr, dom, mon, dow] = parts;
  if (mon !== "*") return { ...fallback, isAdvanced: true };

  // Monthly — a single calendar day, fixed time, no weekday filter.
  // `0 9 15 * *` → "the 15th of every month at 09:00".
  if (dom !== "*") {
    const domNum = Number(dom);
    const minNum = Number(minStr);
    const hourNum = Number(hourStr);
    if (
      /^\d+$/.test(dom) &&
      domNum >= 1 &&
      domNum <= 31 &&
      dow === "*" &&
      /^\d+$/.test(minStr) &&
      minNum >= 0 &&
      minNum <= 59 &&
      /^\d+$/.test(hourStr) &&
      hourNum >= 0 &&
      hourNum <= 23
    ) {
      return {
        ...fallback,
        mode: "monthly",
        dayOfMonth: domNum,
        monthlyTime: { hour: hourNum, minute: minNum },
        isAdvanced: false,
      };
    }
    // A day-of-month that the builder can't model (ranges, steps,
    // combined with a weekday filter, …) → raw cron tab.
    return { ...fallback, isAdvanced: true };
  }

  const dows = parseDow(dow);
  if (dows === null) return { ...fallback, isAdvanced: true };

  // Interval — every minute / every N minutes.
  if (hourStr === "*") {
    if (minStr === "*") {
      return { ...fallback, mode: "interval", intervalValue: 1, intervalUnit: "minutes", dows };
    }
    const m = minStr.match(/^\*\/(\d+)$/);
    if (m) {
      const n = Number(m[1]);
      if (MINUTE_INTERVALS.includes(n)) {
        return {
          ...fallback,
          mode: "interval",
          intervalValue: n,
          intervalUnit: "minutes",
          dows,
        };
      }
    }
  }

  // Interval — every hour / every N hours (only when the minute field
  // is exactly 0 so we don't claim ownership of arbitrary schedules).
  if (minStr === "0") {
    if (hourStr === "*") {
      return { ...fallback, mode: "interval", intervalValue: 1, intervalUnit: "hours", dows };
    }
    const h = hourStr.match(/^\*\/(\d+)$/);
    if (h) {
      const n = Number(h[1]);
      if (HOUR_INTERVALS.includes(n)) {
        return {
          ...fallback,
          mode: "interval",
          intervalValue: n,
          intervalUnit: "hours",
          dows,
        };
      }
    }
  }

  // Specific times — same minute, list of hours.
  const minNum = Number(minStr);
  if (Number.isInteger(minNum) && minNum >= 0 && minNum <= 59) {
    const hours = parseList(hourStr, 0, 23);
    if (hours !== null && hours.length > 0 && hours.length <= MAX_TIMES) {
      return {
        ...fallback,
        mode: "times",
        times: hours.map((h) => ({ hour: h, minute: minNum })),
        dows,
      };
    }
  }

  return { ...fallback, isAdvanced: true };
}
